// src/pages/Admin/Transactions/AdminTransactionDetails.jsx
import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Table, Modal, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaUser, FaCalendar, FaCreditCard, FaInfoCircle } from 'react-icons/fa';
import Sidebar from '../../../components/Common/Sidebar';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const AdminTransactionDetails = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchTransactionDetails();
  }, [transactionId]);

  const fetchTransactionDetails = async () => {
    try {
      setLoading(true);
      // Since we don't have a direct transaction details endpoint in admin API,
      // we'll fetch all transactions and find the specific one
      const response = await adminAPI.transactions.getAll({ 
        search: transactionId,
        per_page: 1 
      });
      
      if (response.data.data.transactions.length > 0) {
        setTransaction(response.data.data.transactions[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch transaction details');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async () => {
    try {
      setVerifying(true);
      await adminAPI.transactions.verifyPayment(transactionId);
      toast.success('Payment verified successfully');
      setShowVerifyModal(false);
      navigate('/admin/transactions');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify payment');
    } finally {
      setVerifying(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: 'success',
      pending: 'warning',
      failed: 'danger',
      refunded: 'info'
    };
    return variants[status] || 'secondary';
  };

  const getTypeBadge = (type) => {
    const variants = {
      deposit: 'success',
      withdrawal: 'danger',
      commission: 'info',
      refund: 'warning'
    };
    return variants[type] || 'secondary';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'success':
        return '✓';
      case 'pending':
        return '⏱';
      case 'failed':
        return '✗';
      case 'refunded':
        return '↺';
      default:
        return '•';
    }
  };

  if (loading) {
    return (
      <div className="d-flex">
        <Sidebar role="admin" />
        <div className="flex-grow-1 p-4">
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--accent-cyan)' }} />
            <p className="mt-3">Loading transaction details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="d-flex">
        <Sidebar role="admin" />
        <div className="flex-grow-1 p-4">
          <div className="text-center py-5">
            <h5>Transaction not found</h5>
            <Button variant="primary" onClick={() => navigate('/admin/transactions')}>
              Back to Transactions
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="dashboard-layout">
  <Sidebar role="admin" />
  <div className="dashboard-content">

        <div className="mb-4">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => navigate('/admin/transactions')}
            className="mb-3"
          >
            <FaArrowLeft className="me-2" />
            Back to Transactions
          </Button>
          
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2 className="fw-bold mb-1">Transaction Details</h2>
              <p className="text-muted mb-0 text-monospace">{transaction.transaction_id}</p>
            </div>
            
            {transaction.status === 'pending' && transaction.transaction_type === 'deposit' && (
              <Button 
                variant="success" 
                onClick={() => setShowVerifyModal(true)}
              >
                <FaCheck className="me-2" />
                Verify Payment
              </Button>
            )}
          </div>
        </div>

        {/* Status Alert */}
        {transaction.status === 'pending' && (
          <Alert variant="warning" className="mb-4">
            <strong>⚠️ Pending Transaction:</strong> This transaction is awaiting verification. 
            Please review the details and verify if the payment has been received.
          </Alert>
        )}

        {transaction.status === 'failed' && (
          <Alert variant="danger" className="mb-4">
            <strong>✗ Failed Transaction:</strong> This transaction has failed. 
            {transaction.description && ` Reason: ${transaction.description}`}
          </Alert>
        )}

        <div className="row g-4">
          {/* Transaction Information */}
          <div className="col-md-8">
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaInfoCircle className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  Transaction Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: '40%' }}>Transaction ID:</td>
                      <td className="fw-bold text-monospace">{transaction.transaction_id}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Type:</td>
                      <td>
                        <Badge bg={getTypeBadge(transaction.transaction_type)} className="px-3 py-2">
                          {transaction.transaction_type.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Status:</td>
                      <td>
                        <Badge bg={getStatusBadge(transaction.status)} className="px-3 py-2">
                          {getStatusIcon(transaction.status)} {transaction.status.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Amount:</td>
                      <td>
                        <h4 className="mb-0 fw-bold" style={{ color: 'var(--accent-cyan)' }}>
                          ₹{parseFloat(transaction.amount).toFixed(2)}
                        </h4>
                      </td>
                    </tr>
                    {transaction.admin_commission > 0 && (
                      <>
                        <tr>
                          <td className="text-muted">Admin Commission:</td>
                          <td className="fw-bold text-success">
                            ₹{parseFloat(transaction.admin_commission).toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Distributor Amount:</td>
                          <td className="fw-bold">
                            ₹{parseFloat(transaction.distributor_amount).toFixed(2)}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td className="text-muted">Payment Gateway:</td>
                      <td className="text-capitalize">
                        {transaction.payment_gateway || 'N/A'}
                      </td>
                    </tr>
                    {transaction.gateway_transaction_id && (
                      <tr>
                        <td className="text-muted">Gateway Transaction ID:</td>
                        <td className="text-monospace">
                          {transaction.gateway_transaction_id}
                        </td>
                      </tr>
                    )}
                    {transaction.description && (
                      <tr>
                        <td className="text-muted">Description:</td>
                        <td>{transaction.description}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-muted">Created At:</td>
                      <td>
                        <FaCalendar className="me-2" />
                        {new Date(transaction.created_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Last Updated:</td>
                      <td>
                        {new Date(transaction.updated_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* Timeline */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">Transaction Timeline</h5>
              </Card.Header>
              <Card.Body>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-badge bg-primary">
                      <FaCreditCard />
                    </div>
                    <div className="timeline-content">
                      <h6 className="mb-1">Transaction Initiated</h6>
                      <p className="text-muted mb-0 small">
                        {new Date(transaction.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {transaction.gateway_transaction_id && (
                    <div className="timeline-item">
                      <div className="timeline-badge bg-info">
                        <FaCheck />
                      </div>
                      <div className="timeline-content">
                        <h6 className="mb-1">Payment Gateway Response</h6>
                        <p className="text-muted mb-0 small">
                          Gateway ID: {transaction.gateway_transaction_id}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="timeline-item">
                    <div className={`timeline-badge bg-${getStatusBadge(transaction.status)}`}>
                      {getStatusIcon(transaction.status)}
                    </div>
                    <div className="timeline-content">
                      <h6 className="mb-1">
                        {transaction.status === 'success' ? 'Transaction Completed' : 
                         transaction.status === 'pending' ? 'Awaiting Verification' :
                         transaction.status === 'failed' ? 'Transaction Failed' :
                         'Transaction Refunded'}
                      </h6>
                      <p className="text-muted mb-0 small">
                        {new Date(transaction.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* User Information */}
          <div className="col-md-4">
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaUser className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  User Information
                </h5>
              </Card.Header>
              <Card.Body>
                {transaction.user ? (
                  <>
                    <div className="text-center mb-3">
                      <div 
                        className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3"
                        style={{ 
                          width: '80px', 
                          height: '80px', 
                          backgroundColor: 'var(--accent-cyan)',
                          fontSize: '32px',
                          fontWeight: 'bold',
                          color: 'var(--bg-dark)'
                        }}
                      >
                        {transaction.user.username?.charAt(0).toUpperCase()}
                      </div>
                      <h5 className="fw-bold mb-1">{transaction.user.username}</h5>
                      <p className="text-muted mb-0 small">{transaction.user.email}</p>
                    </div>

                    <hr />

                    <Table borderless className="mb-0 small">
                      <tbody>
                        <tr>
                          <td className="text-muted">User ID:</td>
                          <td className="text-end fw-bold">#{transaction.user.id}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Role:</td>
                          <td className="text-end">
                            <Badge bg="info">{transaction.user.user_role}</Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status:</td>
                          <td className="text-end">
                            <Badge bg={transaction.user.status === 'approved' ? 'success' : 'warning'}>
                              {transaction.user.status}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Account:</td>
                          <td className="text-end">
                            <Badge bg={transaction.user.account_status === 'active' ? 'success' : 'secondary'}>
                              {transaction.user.account_status || 'inactive'}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Wallet Balance:</td>
                          <td className="text-end fw-bold">₹{transaction.user.wallet_balance}</td>
                        </tr>
                      </tbody>
                    </Table>

                    <div className="d-grid mt-3">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => navigate(`/admin/users/${transaction.user.id}`)}
                      >
                        View User Profile
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted text-center mb-0">User information not available</p>
                )}
              </Card.Body>
            </Card>

            {/* Quick Actions */}
            {transaction.status === 'pending' && (
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0 fw-bold">Quick Actions</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    {transaction.transaction_type === 'deposit' && (
                      <Button 
                        variant="success"
                        onClick={() => setShowVerifyModal(true)}
                      >
                        <FaCheck className="me-2" />
                        Verify Payment
                      </Button>
                    )}
                    <Button 
                      variant="outline-secondary"
                      onClick={() => navigate('/admin/transactions')}
                    >
                      Back to List
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        </div>

        {/* Verify Payment Modal */}
        <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Verify Payment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <div 
                className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  backgroundColor: 'rgba(40, 167, 69, 0.1)'
                }}
              >
                <FaCheck size={40} style={{ color: '#28a745' }} />
              </div>
              <h5>Verify Transaction Payment?</h5>
              <p className="text-muted">
                Transaction ID: <strong>{transaction.transaction_id}</strong>
              </p>
            </div>

            <Alert variant="info">
              <strong>Amount:</strong> ₹{parseFloat(transaction.amount).toFixed(2)}
              <br />
              <strong>User:</strong> {transaction.user?.username}
              <br />
              <strong>Type:</strong> {transaction.transaction_type}
            </Alert>

            <Alert variant="warning" className="mb-0">
              <strong>⚠️ Warning:</strong> This action will credit the amount to user's wallet and cannot be undone. 
              Please ensure payment has been received before verifying.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowVerifyModal(false)}
              disabled={verifying}
            >
              Cancel
            </Button>
            <Button 
              variant="success" 
              onClick={handleVerifyPayment}
              disabled={verifying}
            >
              {verifying ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Verifying...
                </>
              ) : (
                <>
                  <FaCheck className="me-2" />
                  Verify & Credit
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style jsx>{`
        .timeline {
          position: relative;
          padding-left: 50px;
        }

        .timeline-item {
          position: relative;
          padding-bottom: 30px;
        }

        .timeline-item:last-child {
          padding-bottom: 0;
        }

        .timeline-item::before {
          content: '';
          position: absolute;
          left: -35px;
          top: 35px;
          width: 2px;
          height: calc(100% - 35px);
          background: #dee2e6;
        }

        .timeline-item:last-child::before {
          display: none;
        }

        .timeline-badge {
          position: absolute;
          left: -50px;
          top: 0;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          z-index: 1;
        }

        .timeline-content {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
        }

        .timeline-content h6 {
          font-weight: 600;
          color: var(--bg-dark);
        }
      `}</style>
    </div>
  );
};

export default AdminTransactionDetails;